import { useRef, useEffect, useState } from 'react';

import { Button, CloseButton, IconButton } from '@/components/button';
import { Icon } from '@/components/icon';
import { CheckboxWithLabel } from '@/components/form/checkbox';
import { Tooltip } from '@/components/tooltip';

import { isCellWithVisualisations, isRecordsLikeCell, restoreInvalidCell } from '@/core/cell';

import { SidebarSection } from '../../sidebar-section';
import { EditCohort } from '../../../edit-cohort';
import { EditPipeline } from '../../../edit-pipeline';
import { useExplorationContext } from '../../exploration-context';
import { useExplorationUIContext } from '../../exploration-ui-context';
import { useMetadataContext } from '../../../metadata-context';
import { EditFunnel } from '../../../edit-funnel';
import { CombineEditor } from '../../combine';
import { EditVariableCell } from '../../../edit-variable';
import {
  TableModeKey,
  PipelinePreviewKey,
  BlockTitleKey,
  useExplorationCellContext,
} from '../../exploration-cell-context';
import { CellViewOptions } from '../../../types';
import { EditVisualisation } from '../../../edit-visualisation';
import { getCellTitle } from '../../exploration-outline/cell-item';

import styles from '../sidebar.module.scss'; // TODO: move to ./cell-sidebar.module.scss

export const CellSidebar = () => {
  const { models, metrics } = useMetadataContext();

  const { exploration, setExploration, getVariables, getQueryMeta } = useExplorationContext();
  const {
    selectedCell,
    closeEditor,
    openVisualisationEditor,
    openVisualisationSections,
    setOpenVisualisationSections,
  } = useExplorationUIContext();
  const {
    cell,
    tableMode,
    addDefaultVisualization,
    removeVisualisation,
    setVisualisation,
    setCellViewOption,
    isPipelinePreviewVisible,
    isBlockTitleVisible,
    isTableVisible,
    hideTable,
    showTable,
    addPipelineOperation,
    removePipelineOperation,
    removeParentPipelineOperation,
    updatePipelineOperation,
    updateParentPipelineOperation,
    reorderPipelineOperation,
    disablePipelineOperation,
    disableParentPipelineOperation,
    detachPipeline,
    changeBasePipeline,
    addCombine,
    updateCombine,
    setRollbackIndex,
    clearRollback,
    getCellViewOption,
    pipelineWithRollback,
  } = useExplorationCellContext();

  const [showCombine, setShowCombine] = useState(false);

  const visualisations = isCellWithVisualisations(cell) ? (cell.visualisations ?? []) : [];
  const unwrappedCell = restoreInvalidCell(cell);
  const prevVisualisationsLength = useRef(visualisations.length);

  // When selection changes, dismiss the add join panel
  useEffect(() => {
    setShowCombine(false);
  }, [selectedCell]);

  // Open last visualisation if the amount of visualisations has increased
  useEffect(() => {
    prevVisualisationsLength.current = visualisations.length;
  }, [visualisations.length]);

  // Reset the state immediately after the section's state has been initialized
  useEffect(() => {
    if (openVisualisationSections.length !== 0) {
      setOpenVisualisationSections([]);
    }
  }, [openVisualisationSections.length, setOpenVisualisationSections]);

  const selectedQueryMeta = getQueryMeta(cell.id);
  const fields = selectedQueryMeta?.recordType ?? [];
  const grouping = selectedQueryMeta?.grouping?.map(({ key }) => key) ?? [];

  const showCombineEditor = () => {
    if (selectedCell === null) {
      return;
    }

    setShowCombine(true);
  };

  const handleAddVisualisation = () => {
    const idx = visualisations.length;
    addDefaultVisualization();
    openVisualisationEditor(idx);
  };

  return (
    <>
      <div className={styles.sidebarHeader}>
        <h1>{getCellTitle(cell)}</h1>
        <CloseButton onClick={closeEditor} iconSize="regular" className={styles.closeButton} />
      </div>
      <div className={styles.scrollContainer} key={cell.id}>
        {showCombine && (
          <SidebarSection title="Pipeline">
            <CombineEditor
              onSubmit={() => setShowCombine(false)}
              onCancel={() => setShowCombine(false)}
              onAddCombine={addCombine}
              onUpdateCombine={updateCombine}
            />
          </SidebarSection>
        )}

        {!showCombine && isRecordsLikeCell(cell) && (
          <>
            <SidebarSection title="Pipeline" collapsible={false}>
              <EditPipeline
                title={cell.title ?? exploration.name}
                pipeline={cell.pipeline}
                recordType={fields}
                grouping={grouping}
                rollbackIndex={
                  (getCellViewOption('rollback') as CellViewOptions['rollback'] | undefined)?.index
                }
                onCombine={showCombineEditor}
                onAddOperation={addPipelineOperation}
                onRemoveOperation={removePipelineOperation}
                onRemoveParentOperation={removeParentPipelineOperation}
                onUpdateOperation={updatePipelineOperation}
                onUpdateParentOperation={updateParentPipelineOperation}
                onReorderOperation={reorderPipelineOperation}
                onDisableOperation={disablePipelineOperation}
                onDisableParentOperation={disableParentPipelineOperation}
                onDetachPipeline={detachPipeline}
                onBasePipelineChange={changeBasePipeline}
                onAddCombine={addCombine}
                onUpdateCombine={updateCombine}
                onRollback={setRollbackIndex}
                onResetRollback={clearRollback}
              />
            </SidebarSection>
            <SidebarSection iconName="Table" title="Display" collapsible collapsed>
              <CheckboxWithLabel
                checked={isBlockTitleVisible}
                onChange={() =>
                  setCellViewOption(BlockTitleKey, isBlockTitleVisible ? 'hidden' : 'visible')
                }>
                Show block title
              </CheckboxWithLabel>
              <CheckboxWithLabel
                checked={isPipelinePreviewVisible}
                onChange={() =>
                  setCellViewOption(
                    PipelinePreviewKey,
                    isPipelinePreviewVisible ? 'hidden' : 'visible',
                  )
                }>
                Show steps
              </CheckboxWithLabel>
              <Tooltip
                placement="left"
                content={
                  visualisations.length === 0
                    ? 'Cannot hide the table of a block with no charts'
                    : undefined
                }>
                <CheckboxWithLabel
                  checked={isTableVisible}
                  disabled={visualisations.length === 0}
                  onChange={({ checked }) => (checked ? showTable() : hideTable())}>
                  Show table
                </CheckboxWithLabel>
              </Tooltip>
              {isTableVisible ? (
                <CheckboxWithLabel
                  checked={tableMode === 'cards'}
                  onChange={() =>
                    setCellViewOption(TableModeKey, tableMode === 'cards' ? 'table' : 'cards')
                  }>
                  Show rows as cards
                </CheckboxWithLabel>
              ) : null}
            </SidebarSection>
            {visualisations.map((visualisation: any, idx: any) => (
              <SidebarSection
                key={idx}
                iconName="Graphic"
                title={`Chart ${idx + 1}`}
                titleActions={
                  <IconButton
                    icon="Trash2"
                    title="Remove chart"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeVisualisation(idx);
                    }}
                  />
                }
                collapsible
                collapsed={!openVisualisationSections.includes(idx)}>
                <EditVisualisation
                  visualisation={visualisation}
                  pipeline={pipelineWithRollback ?? cell.pipeline}
                  models={models}
                  metrics={metrics}
                  variables={getVariables()}
                  onChange={(updatedVisualisation) => setVisualisation(idx, updatedVisualisation)}
                />
              </SidebarSection>
            ))}
            <SidebarSection>
              <Button
                icon={<Icon name="Graphic" />}
                size="compact"
                variant="gray"
                onClick={handleAddVisualisation}>
                Add chart
              </Button>
            </SidebarSection>
          </>
        )}
        {unwrappedCell.kind === 'funnel' && (
          <EditFunnel
            title={exploration.name}
            pipeline={unwrappedCell.pipeline}
            setExploration={setExploration}
            exploration={exploration}
          />
        )}
        {unwrappedCell.kind === 'cohort' && (
          <EditCohort setExploration={setExploration} pipeline={unwrappedCell.pipeline} />
        )}
        {cell.kind === 'variable' && (
          <EditVariableCell cell={cell} setExploration={setExploration} />
        )}
      </div>
    </>
  );
};
